import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePartnerRequests } from '../hooks/usePartnerRequests';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { hasAnyPermission, Permission } from '../utils/roles';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  border-radius: 10px;
  border: 1px solid ${({ $variant }) => ($variant === 'primary' ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $variant }) => ($variant === 'primary' ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $variant }) => ($variant === 'primary' ? 'var(--surface)' : 'var(--onSurface)')};
  padding: 0.5rem 0.95rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ $variant }) => ($variant === 'primary' ? '#1a63d6' : 'var(--surfaceVariant)')};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    vertical-align: top;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const ClickableRow = styled.tr<{ $clickable?: boolean }>`
  ${({ $clickable }) =>
    $clickable
      ? `
    cursor: pointer;
    &:hover {
      background: var(--surfaceVariant);
    }
  `
      : ''}
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 1rem;
  font-size: 0.9rem;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  margin: 0 0 1rem;
  align-items: flex-end;
`;

const FilterGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--onSurfaceVariant);
`;

const DateInput = styled.input`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  color: var(--onSurface);
  background: var(--surface);
`;

const ClearButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.5rem 0.9rem;
  font-size: 0.85rem;
  color: var(--onSurface);
  background: var(--surface);
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  background: var(--outline);
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: min(720px, 100%);
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--outline);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  color: var(--onSurface);
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-size: 1.1rem;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1.25rem 1.5rem 1.5rem;
`;

const UploadCard = styled.div`
  background: var(--surfaceVariant);
  border-radius: 12px;
  border: 1px dashed #cbd5f5;
  padding: 1rem;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const UploadFileName = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

const UploadErrorList = styled.ul`
  margin: 0.75rem 0 0;
  padding-left: 1.25rem;
  color: #c53030;
  font-size: 0.85rem;
`;

const UploadPreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.85rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: #f1f5f9;
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Label = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--onSurfaceVariant);
`;

type CsvPartnerRow = {
  rowNumber: number;
  name: string;
  email: string;
  phone: string | null;
  dob: string | null;
  address: string | null;
  country: string | null;
  avatarUrl: string | null;
  howJoined: 'online' | 'in-person' | null;
  createdAt: string | null;
};

type CsvParseResult = {
  rows: CsvPartnerRow[];
  errors: string[];
};

type CsvUploadResult = {
  inserted: number;
};

type CsvAuthResult = {
  created: number;
  skipped: number;
  failed: number;
  errors?: { row: number; message: string }[];
};

const normalizeCsvHeader = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const parseCsvText = (raw: string): string[][] => {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentValue = '';
  let inQuotes = false;

  for (let i = 0; i < raw.length; i += 1) {
    const char = raw[i];
    const nextChar = raw[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentValue += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      currentRow.push(currentValue);
      currentValue = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i += 1;
      }
      currentRow.push(currentValue);
      if (currentRow.some((cell) => cell.trim() !== '')) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentValue = '';
      continue;
    }

    currentValue += char;
  }

  if (currentValue.length || currentRow.length) {
    currentRow.push(currentValue);
    if (currentRow.some((cell) => cell.trim() !== '')) {
      rows.push(currentRow);
    }
  }

  return rows;
};

const normalizePhoneInput = (value: string) => {
  const cleaned = value.replace(/[^\d+]/g, '');
  if (!cleaned) return null;
  const digits = cleaned.replace(/\D/g, '');
  const candidate = cleaned.startsWith('+')
    ? cleaned
    : digits.length === 10
      ? `+1${digits}`
      : `+${digits}`;
  return /^\+[1-9]\d{6,14}$/.test(candidate) ? candidate : null;
};

const normalizeHowJoined = (value: string) => {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  if (['online', 'web', 'website'].includes(trimmed)) return 'online';
  if (['in-person', 'in person', 'inperson', 'in_person'].includes(trimmed)) return 'in-person';
  return null;
};

const normalizeCreatedAt = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const buildCsvRows = (raw: string): CsvParseResult => {
  const rows = parseCsvText(raw);
  if (!rows.length) {
    return { rows: [], errors: ['No rows found in the CSV file.'] };
  }

  const headerRow = rows[0].map((header) => normalizeCsvHeader(header));
  const headerLookup = (aliases: string[]) =>
    headerRow.findIndex((header) => aliases.includes(header));

  const nameIndex = headerLookup(['name', 'full name', 'full_name']);
  const emailIndex = headerLookup(['email', 'email address', 'email_address']);
  const phoneIndex = headerLookup(['phone', 'phone number', 'mobile', 'mobile number']);
  const dobIndex = headerLookup(['dob', 'date of birth', 'birthdate']);
  const addressIndex = headerLookup(['address', 'street address', 'home address']);
  const countryIndex = headerLookup(['country']);
  const avatarIndex = headerLookup(['avatar', 'avatar url', 'avatar_url', 'photo', 'photo url', 'image', 'image url']);
  const howJoinedIndex = headerLookup(['how joined', 'how_joined', 'joined', 'join method', 'join_method', 'source']);
  const createdAtIndex = headerLookup(['submitted', 'submitted at', 'created at', 'created_at', 'date']);

  if (nameIndex < 0) {
    return { rows: [], errors: ['Missing required column: name.'] };
  }

  if (emailIndex < 0) {
    return { rows: [], errors: ['Missing required column: email.'] };
  }

  const parsedRows: CsvPartnerRow[] = [];
  const errors: string[] = [];

  rows.slice(1).forEach((row, index) => {
    const rowNumber = index + 2;
    const rawName = row[nameIndex] ?? '';
    const rawEmail = row[emailIndex] ?? '';
    const rawPhone = phoneIndex >= 0 ? row[phoneIndex] ?? '' : '';
    const rawDob = dobIndex >= 0 ? row[dobIndex] ?? '' : '';
    const rawAddress = addressIndex >= 0 ? row[addressIndex] ?? '' : '';
    const rawCountry = countryIndex >= 0 ? row[countryIndex] ?? '' : '';
    const rawAvatar = avatarIndex >= 0 ? row[avatarIndex] ?? '' : '';
    const rawHowJoined = howJoinedIndex >= 0 ? row[howJoinedIndex] ?? '' : '';
    const rawCreatedAt = createdAtIndex >= 0 ? row[createdAtIndex] ?? '' : '';

    const name = rawName.trim();
    const normalizedEmail = rawEmail.trim().toLowerCase();
    const email = normalizedEmail && /^\S+@\S+\.\S+$/.test(normalizedEmail) ? normalizedEmail : '';
    const phone = rawPhone.trim() ? normalizePhoneInput(rawPhone.trim()) : null;
    const dob = rawDob.trim() || null;
    const address = rawAddress.trim() || null;
    const country = rawCountry.trim() || null;
    const avatarUrl = rawAvatar.trim() || null;
    const howJoined = rawHowJoined.trim() ? normalizeHowJoined(rawHowJoined) : null;
    const createdAt = rawCreatedAt.trim() ? normalizeCreatedAt(rawCreatedAt) : null;

    if (!name) {
      errors.push(`Row ${rowNumber}: name is required.`);
      return;
    }

    if (!email) {
      errors.push(`Row ${rowNumber}: email address is invalid.`);
      return;
    }

    if (rawPhone.trim() && !phone) {
      errors.push(`Row ${rowNumber}: phone number is invalid.`);
      return;
    }

    if (rawHowJoined.trim() && !howJoined) {
      errors.push(`Row ${rowNumber}: how joined must be Online or In-person.`);
      return;
    }

    if (rawCreatedAt.trim() && !createdAt) {
      errors.push(`Row ${rowNumber}: submitted date is invalid.`);
      return;
    }

    parsedRows.push({
      rowNumber,
      name,
      email,
      phone,
      dob,
      address,
      country,
      avatarUrl,
      howJoined,
      createdAt,
    });
  });

  return { rows: parsedRows, errors };
};

export const PartnerRequestsPage: React.FC = () => {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageVolunteers = hasAnyPermission(roles, [Permission.MANAGE_VOLUNTEERS]);
  const { data, isLoading, error } = usePartnerRequests();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvRows, setCsvRows] = useState<CsvPartnerRow[]>([]);
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [csvParsing, setCsvParsing] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState<CsvUploadResult | null>(null);
  const [csvAuthCreating, setCsvAuthCreating] = useState(false);
  const [csvAuthResult, setCsvAuthResult] = useState<CsvAuthResult | null>(null);
  const [addAccountLoadingId, setAddAccountLoadingId] = useState<string | null>(null);
  const [addAccountError, setAddAccountError] = useState<string | null>(null);

  useEffect(() => {
    if (showUploadModal) return;
    setCsvFileName(null);
    setCsvRows([]);
    setCsvErrors([]);
    setCsvParsing(false);
    setCsvUploading(false);
    setCsvResult(null);
    setCsvAuthCreating(false);
    setCsvAuthResult(null);
  }, [showUploadModal]);

  const filteredRequests = useMemo(() => {
    if (!data?.length) return data ?? [];
    if (!startDate && !endDate) return data;

    const startBoundary = startDate ? new Date(`${startDate}T00:00:00.000`) : null;
    const endBoundary = endDate ? new Date(`${endDate}T23:59:59.999`) : null;

    return data.filter((request) => {
      if (!request.created_at) {
        return false;
      }
      const createdAt = new Date(request.created_at);
      if (startBoundary && createdAt < startBoundary) return false;
      if (endBoundary && createdAt > endBoundary) return false;
      return true;
    });
  }, [data, startDate, endDate]);

  const openCsvPicker = useCallback(() => {
    csvInputRef.current?.click();
  }, []);

  const handleCsvFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFileName(file.name);
    setCsvParsing(true);
    setCsvResult(null);

    const reader = new FileReader();
    reader.onload = () => {
      const raw = typeof reader.result === 'string' ? reader.result : '';
      const result = buildCsvRows(raw);
      setCsvRows(result.rows);
      setCsvErrors(result.errors);
      setCsvParsing(false);
    };
    reader.onerror = () => {
      setCsvRows([]);
      setCsvErrors(['Failed to read the CSV file.']);
      setCsvParsing(false);
    };
    reader.readAsText(file);
    event.target.value = '';
  }, []);

  const handleUploadCsv = useCallback(async () => {
    if (!csvRows.length || csvErrors.length) return;
    setCsvUploading(true);
    setCsvResult(null);
    setCsvAuthResult(null);

    try {
      const payload = csvRows.map((row) => {
        const entry: {
          name: string;
          email: string;
          phone: string | null;
          dob: string | null;
          address: string | null;
          country: string | null;
          avatar_url: string | null;
          how_joined: string | null;
          created_at?: string;
        } = {
          name: row.name,
          email: row.email,
          phone: row.phone,
          dob: row.dob,
          address: row.address,
          country: row.country,
          avatar_url: row.avatarUrl,
          how_joined: row.howJoined,
        };

        if (row.createdAt) {
          entry.created_at = row.createdAt;
        }

        return entry;
      });

      const { error: insertError } = await supabase.from('partner_requests').insert(payload);
      if (insertError) {
        throw insertError;
      }

      setCsvResult({ inserted: csvRows.length });
      await queryClient.invalidateQueries({ queryKey: ['partnerRequests'] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload partner submissions.';
      setCsvErrors([message]);
    } finally {
      setCsvUploading(false);
    }
  }, [csvErrors.length, csvRows, queryClient]);

  const handleCreateAuthUsers = useCallback(async () => {
    if (!csvRows.length || csvErrors.length) return;
    setCsvAuthCreating(true);
    setCsvAuthResult(null);
    try {
      const payload = csvRows.map((row) => ({
        name: row.name,
        email: row.email,
        phone: row.phone,
        row_number: row.rowNumber,
      }));
      const { data, error } = await supabase.functions.invoke('create-partner-auth-users', {
        body: { rows: payload },
      });
      if (error) throw error;
      if (data && (data as { error?: string }).error) {
        throw new Error((data as { error: string }).error);
      }
      setCsvAuthResult(data as CsvAuthResult);
      await queryClient.invalidateQueries({ queryKey: ['partnerRequests'] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create auth users.';
      setCsvAuthResult({ created: 0, skipped: 0, failed: csvRows.length, errors: [{ row: 0, message }] });
    } finally {
      setCsvAuthCreating(false);
    }
  }, [csvErrors.length, csvRows, queryClient]);

  const handleAddAccount = useCallback(
    async (request: { id: string; name?: string | null; email?: string | null; phone?: string | null }) => {
      const email = request.email?.trim() || null;
      const phone = request.phone?.trim() ? normalizePhoneInput(request.phone.trim()) : null;
      if (!email && !phone) {
        setAddAccountError('Email or phone required to create account.');
        return;
      }
      setAddAccountError(null);
      setAddAccountLoadingId(request.id);
      try {
        const { data, error } = await supabase.functions.invoke('create-partner-auth-users', {
          body: {
            rows: [
              {
                name: request.name ?? undefined,
                email: email ?? undefined,
                phone: phone ?? undefined,
                row_number: 1,
              },
            ],
          },
        });
        if (error) throw error;
        if (data && (data as { error?: string }).error) {
          throw new Error((data as { error: string }).error);
        }
        const result = data as CsvAuthResult;
        if (result.failed > 0 && result.errors?.length) {
          throw new Error(result.errors[0].message);
        }
        await queryClient.invalidateQueries({ queryKey: ['partnerRequests'] });
      } catch (err) {
        setAddAccountError(err instanceof Error ? err.message : 'Failed to create account.');
      } finally {
        setAddAccountLoadingId(null);
      }
    },
    [queryClient]
  );

  const formatHowJoined = (value?: string | null) => {
    if (!value) return '—';
    const normalized = value.toLowerCase();
    if (normalized === 'online') return 'Online';
    if (normalized === 'in-person' || normalized === 'in person' || normalized === 'in_person') return 'In-person';
    return value;
  };

  const handleDownloadCsv = useCallback(() => {
    const escapeCsv = (val: string | null | undefined): string => {
      const s = val == null ? '' : String(val).trim();
      if (s === '') return '';
      const needsQuotes = /[",\n\r]/.test(s);
      return needsQuotes ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['Name', 'Email', 'Phone', 'DOB', 'Address', 'Country', 'Avatar URL', 'How Joined', 'Submitted'];
    const rows = filteredRequests.map((r) => [
      escapeCsv(r.name),
      escapeCsv(r.email),
      escapeCsv(r.phone ?? null),
      escapeCsv(r.dob ?? null),
      escapeCsv(r.address ?? null),
      escapeCsv(r.country ?? null),
      escapeCsv(r.avatar_url ?? null),
      escapeCsv(r.how_joined ?? null),
      r.created_at ? escapeCsv(new Date(r.created_at).toISOString()) : '',
    ]);
    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partner-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredRequests]);

  const handleDownloadPartnershipGuide = useCallback(() => {
    const guideUrl = '/docs/PartnershipGuide.pdf';

    const openedTab = window.open(guideUrl, '_blank', 'noopener,noreferrer');
    if (openedTab) {
      return;
    }

    // Fallback when pop-up/new-tab open is blocked by browser settings.
    const a = document.createElement('a');
    a.href = guideUrl;
    a.download = 'PartnershipGuide.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  if (isLoading) return <p>Loading partner submissions…</p>;
  if (error) return <p>Error loading partner submissions.</p>;

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Partner Submissions</HeaderTitle>
        <HeaderActions>
          <ActionButton type="button" $variant="primary" onClick={handleDownloadPartnershipGuide}>
            Download Partnership Guide
          </ActionButton>
          <ActionButton type="button" $variant="primary" onClick={handleDownloadCsv}>
            Download CSV
          </ActionButton>
          {canManageVolunteers ? (
            <ActionButton type="button" $variant="primary" onClick={() => setShowUploadModal(true)}>
              Upload CSV
            </ActionButton>
          ) : null}
        </HeaderActions>
      </HeaderRow>
      <HelperText>
        Requests submitted from the mobile app and the Partner form.
      </HelperText>
      <FiltersRow>
        <FilterGroup>
          Start date
          <DateInput
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </FilterGroup>
        <FilterGroup>
          End date
          <DateInput
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </FilterGroup>
        {(startDate || endDate) && (
          <ClearButton
            type="button"
            onClick={() => {
              setStartDate('');
              setEndDate('');
            }}
          >
            Clear dates
          </ClearButton>
        )}
      </FiltersRow>
      {addAccountError && (
        <HelperText style={{ color: '#c53030', marginBottom: '0.5rem' }}>
          {addAccountError}
          <button
            type="button"
            onClick={() => setAddAccountError(null)}
            style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Dismiss
          </button>
        </HelperText>
      )}
      <TableCard>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Avatar</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>DOB</th>
              <th>Address</th>
              <th>Country</th>
              <th>How Joined</th>
              <th>Submitted</th>
              <th>Account</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((request) => {
              const memberPath = request.user_id ? `/admin/members/${request.user_id}` : null;
              const handleNavigate = () => {
                if (memberPath) {
                  navigate(memberPath);
                }
              };
              return (
                <ClickableRow
                  key={request.id}
                  $clickable={Boolean(memberPath)}
                  onClick={handleNavigate}
                  onKeyDown={(event) => {
                    if (!memberPath) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleNavigate();
                    }
                  }}
                  role={memberPath ? 'link' : undefined}
                  tabIndex={memberPath ? 0 : -1}
                >
                <td>
                  {request.avatar_url ? (
                    <Avatar src={request.avatar_url} alt={request.name ?? 'User avatar'} />
                  ) : (
                    '—'
                  )}
                </td>
                <td>{request.name ?? '—'}</td>
                <td>{request.email ?? '—'}</td>
                <td>{request.phone ?? '—'}</td>
                <td>{request.dob ?? '—'}</td>
                <td>{request.address ?? '—'}</td>
                <td>{request.country ?? '—'}</td>
                <td>{formatHowJoined(request.how_joined)}</td>
                <td>{request.created_at ? new Date(request.created_at).toLocaleString() : '—'}</td>
                <td>
                  {request.user_id ? (
                    <ActionButton
                      type="button"
                      $variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/members/${request.user_id}`);
                      }}
                    >
                      View
                    </ActionButton>
                  ) : canManageVolunteers ? (
                    <ActionButton
                      type="button"
                      $variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddAccount(request);
                      }}
                      disabled={
                        addAccountLoadingId === request.id ||
                        (!(request.email?.trim()) && !(request.phone?.trim()))
                      }
                    >
                      {addAccountLoadingId === request.id ? 'Adding…' : 'Add account'}
                    </ActionButton>
                  ) : (
                    '—'
                  )}
                </td>
                </ClickableRow>
              );
            })}
          </tbody>
          </Table>
        </TableWrap>
      </TableCard>
      {showUploadModal && (
        <ModalOverlay onClick={() => setShowUploadModal(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Upload partner submissions</ModalTitle>
              <ModalCloseButton type="button" onClick={() => setShowUploadModal(false)}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <HelperText>
                Upload a CSV with columns: Name, Email, Phone, DOB, Address, Country, Avatar URL, How Joined, Submitted.
              </HelperText>
              <UploadCard style={{ marginTop: '0.85rem' }}>
                <UploadRow>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: 'none' }}
                    onChange={handleCsvFileChange}
                  />
                  <ActionButton type="button" $variant="primary" onClick={openCsvPicker}>
                    Choose CSV
                  </ActionButton>
                  {csvFileName ? <UploadFileName>{csvFileName}</UploadFileName> : null}
                </UploadRow>
                {csvParsing ? <HelperText>Parsing CSV...</HelperText> : null}
                {csvRows.length > 0 ? (
                  <HelperText>Parsed {csvRows.length} rows ready for upload.</HelperText>
                ) : null}
                {csvErrors.length > 0 && (
                  <UploadErrorList>
                    {csvErrors.slice(0, 6).map((err, index) => (
                      <li key={`${err}-${index}`}>{err}</li>
                    ))}
                  </UploadErrorList>
                )}
              </UploadCard>

              {csvRows.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <Label>Preview (first 5 rows)</Label>
                  <UploadPreviewTable style={{ marginTop: '0.5rem' }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Avatar</th>
                        <th>How Joined</th>
                        <th>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvRows.slice(0, 5).map((row) => (
                        <tr key={row.rowNumber}>
                          <td>{row.name}</td>
                          <td>{row.email}</td>
                          <td>{row.phone ?? '-'}</td>
                          <td>{row.avatarUrl ? 'Yes' : '-'}</td>
                          <td>{row.howJoined ? formatHowJoined(row.howJoined) : '-'}</td>
                          <td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </UploadPreviewTable>
                </div>
              )}

              {csvResult && (
                <div style={{ marginTop: '1rem' }}>
                  <HelperText>Uploaded: {csvResult.inserted}</HelperText>
                </div>
              )}

              {csvAuthResult && (
                <div style={{ marginTop: '1rem' }}>
                  <HelperText>
                    Auth users — Created: {csvAuthResult.created} | Skipped: {csvAuthResult.skipped} | Failed:{' '}
                    {csvAuthResult.failed}
                  </HelperText>
                  {csvAuthResult.errors?.length ? (
                    <UploadErrorList>
                      {csvAuthResult.errors.slice(0, 6).map((err, index) => (
                        <li key={`${err.row}-${err.message}-${index}`}>
                          Row {err.row}: {err.message}
                        </li>
                      ))}
                    </UploadErrorList>
                  ) : null}
                </div>
              )}

              <ActionRow style={{ marginTop: '1rem' }}>
                <ActionButton
                  $variant="primary"
                  type="button"
                  onClick={handleUploadCsv}
                  disabled={csvUploading || csvErrors.length > 0 || csvRows.length === 0}
                >
                  {csvUploading ? 'Uploading...' : 'Upload submissions'}
                </ActionButton>
                <ActionButton
                  type="button"
                  $variant="ghost"
                  onClick={handleCreateAuthUsers}
                  disabled={
                    csvAuthCreating ||
                    csvUploading ||
                    csvErrors.length > 0 ||
                    csvRows.length === 0
                  }
                >
                  {csvAuthCreating ? 'Creating users...' : 'Create auth users'}
                </ActionButton>
                <ActionButton type="button" $variant="ghost" onClick={() => setShowUploadModal(false)}>
                  Cancel
                </ActionButton>
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default PartnerRequestsPage;
